import { Component, OnInit, OnDestroy, signal, computed } from '@angular/core';
import { CommonModule, DatePipe, Location } from '@angular/common';
import { ReactiveFormsModule, AbstractControl, UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import responsibleLocationKeyPerCountryKey from './../../../files/responsibleLocationKeyPerCountryKey.json';
import { PaxOrderService } from '../../services/pax-order.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedService } from '../../services/shared.service';
import { Store } from '@ngrx/store';
import { selectAccessToken } from 'src/app/store/auth.selectors';
import { CheckUserRolesService } from '../../services/check-user-roles.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import tenantProperties from './../../../files/tenantProperties.json';
import { FadingMessagePopupComponent } from '../fading-message-popup/fading-message-popup.component';
declare var $: any;

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    FadingMessagePopupComponent
  ],
  selector: 'app-create-pax-order',
  templateUrl: './create-pax-order.component.html',
  styleUrls: ['./create-pax-order.component.scss'],
  providers: [
    PaxOrderService,
    DatePipe
  ]
})
export class CreatePaxOrderComponent implements OnInit, OnDestroy {
  public responsibleLocationKeyPerCountryKeyString: { countryKey: string, responsibleLocationKey: any }[] = responsibleLocationKeyPerCountryKey;

  // Form state signals
  readonly isSubmitting = signal(false);
  readonly submitted = signal(false);
  readonly editSelectedOrder = signal(false);
  readonly createNewOrder = signal(true);

  private submissionTimeout: any;
  private destroy$ = new Subject<void>();
  /**Initialization of form*/
  createOrderForm: UntypedFormGroup;
  showBackButton: boolean = false;
  incoterm = [10, 11, 12, 15, 17, 18, 20, 21, 30, 31, 33, 40, 41, 50, 60, 70, 80, 81, 83, 91, 98, 99];
  tenantKey: string;

  // Date validation signals
  readonly validMinOrderRequestedPickUpDate = signal<any>("");
  readonly validMaxOrderRequestedPickUpDate = signal<any>("");
  readonly currentTime = signal<any>("");

  // Alert signals
  readonly successAlert = signal(false);
  readonly dangerAlert = signal(false);
  readonly invalidAlert = signal(false);
  readonly invalidValidationAlert = signal(false);

  recipientCountryDisabled: boolean = false;
  readonly errorMessage = signal<any[]>([]);
  PHONE_PATTERN: RegExp = /^(?:00|\+)[0-9\s./-]{4,}$/;

  // Country list signals
  readonly requesterCountryList = signal<any[]>([]);
  readonly senderCountryList = signal<any[]>([]);
  readonly recipientCountryList = signal<any[]>([]);

  // Signal to track requester country code (synced with form)
  readonly requesterCountryCode = signal<string>('');

  // Computed filtered sender list
  readonly filteredSenderCountryList = computed(() => {
    const requesterCode = this.requesterCountryCode();
    return this.senderCountryList().filter(
      country => country.code !== requesterCode
    );
  });

  loginDisplay = false;
  readonly maxParcelWeight = signal<any>(40);
  selectedSenderTenant: string;
  selectedTenantCountryObject: any;
  selectedValidRecipientTenantsObject: any;
  validRecipientTenants: any;
  validRecipientCountrycodesOfEach: any;
  validTenantTomorrowPickUpCutOffTime: any;
  isWeekend: boolean = false;
  responsibleLocationString: any;
  paxOrder: any = [];
  paxOrderDetails: any = null;
  paxOrderData: any = null;
  paxOrderDetailsForEdit: any = null;

  readonly isGroupAdmin = signal(false);

  orderAsTemplate: any;

  lastCreatedPaxOrderKey: any;
  slecetedPaxOrderKeyForEditOrder: string = '';
  paxOrderValdationsErrorList: any | null = '';

  //Fading popup message variables
  readonly message = signal<string>('');
  readonly messageType = signal<'success' | 'error'>('success');
  readonly displayDuration = signal(3000);
  readonly fadeDuration = signal(3000);
  accessToken$: any;

  // Date handling properties (ISRS-17834)
  private processingDateBlur: boolean = false;
  private lastUserDateInput: string = ''; // Store user's original input before Angular Material interference

  constructor(private formBuilder: UntypedFormBuilder,
              private checkUserRolesService: CheckUserRolesService,
              private paxOrderService: PaxOrderService,
              private route: ActivatedRoute,
              private sharedService: SharedService,
              private router: Router,
              private location: Location,
              private store: Store,
              private datePipe: DatePipe) {
              this.accessToken$ = this.store.select(selectAccessToken);
              }

  /**Custom Validator for inputs to restrict whitespace*/
  public noWhitespaceValidator(control: UntypedFormControl) {
    const isWhitespace = (control.value || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { 'whitespace': true };
  }

  public paxOrderErrorValidatior(control: UntypedFormControl) { //Only used for highlighting the wrong columns in the begining
    if (!control.parent) {
      return null;
    }
    return control.parent.dirty ? null : { 'validationError': true };
  }

  public noNationalPaxOrders(control: UntypedFormControl) {
    if (!control.parent) {
      return null;
    }
    return control.value === control.parent.get('requesterCountryCode')?.value ? { 'nationalPaxOrder': true } : null;
  }

  public partnerParcelNumberValidator(control: UntypedFormControl) {
    if (!control.parent || !control.value) {
      return null;
    }
    return control.value.split(',').length === control.parent.get('orderNumberOfParcels')?.value ? null : { 'partnerParcelNumber': true };
  }

  /**Custom Validator for inputs to restrict saturday and sunday for orderRequestedPickUpDate*/
  public weekendValidator(control: UntypedFormControl) {
    let date = new Date(control.value);
    if (date.getDay() == 0 || date.getDay() == 6) {
      const weekend = true;
      return { 'weekend': true }
    }
    return null;
  }

  ngOnInit(): void {
    this.sharedService.currentOrderAsTemplate.subscribe(orderAsTemplate => {
      this.orderAsTemplate = orderAsTemplate;
    });
    this.tenantKey = this.checkUserRolesService.tenantKey;
    this.initForm();
    this.getStandardDate();
    this.getCurrentTime();
    this.validRequesterList();
    this.validSenderList();
    this.setDefaultRequesterCountry();
    this.setDefaultSenderCountry();
    this.validRecipientList(this.senderCountryList()[0]);
    this.fillFormWithEditPaxOrderData();
    this.fillFormWithStoredPaxOrderData();
    this.fillFormFromOrderDetailsTemplate();
    this.subscribeForCountryChanges();
    this.subscribeForNumberOfParcels();
    this.isGroupAdmin.set(this.checkUserRolesService.isGroupAccount);

    // Check if the user came from the "Use as template" action
    const fromTemplateAction = localStorage.getItem("fromTemplateAction");
    if (fromTemplateAction) {
      this.showBackButton = true;
      localStorage.removeItem("fromTemplateAction");
    }

    // Initialize requesterCountryCode signal with current form value
    this.requesterCountryCode.set(this.createOrderForm.get('requesterCountryCode')?.value || '');
  }

  // Check if requester country code changes and validate the sender country code
  private subscribeForCountryChanges() {
    this.createOrderForm.get('requesterCountryCode')?.valueChanges.subscribe((selectedRequesterCode: string) => {
      // Update the signal to trigger computed signal recalculation
      this.requesterCountryCode.set(selectedRequesterCode || '');

      this.createOrderForm.get('senderCountryCode')?.updateValueAndValidity();

      // Check if current sender country matches requester country and clear it
      const currentSenderCode = this.createOrderForm.get('senderCountryCode')?.value;
      if (currentSenderCode === selectedRequesterCode) {
        this.createOrderForm.patchValue({ senderCountryCode: '' });
        // Clear the selectedSenderTenant when sender country is cleared
        this.selectedSenderTenant = '';
      }
    });
  }

  private subscribeForNumberOfParcels() {
    this.createOrderForm.get('orderNumberOfParcels')?.valueChanges.subscribe((value) => {
        if (value !== null) {
            this.createOrderForm.get('partnerParcelNumber')?.updateValueAndValidity();
        }
      });
  }

  /** Function to get value of country code from Recipient Tenant and calling the getMaxWeightThreshold function */
  onChangeRecipientCountry(event: any) {
    this.getMaxWeightThreshold(event.target.value);
  }

  /** Function to get max weight threshold from Recipient Tenant */
  getMaxWeightThreshold(selectedRecipientCountry: any) {
    const selectedCountryCode = typeof selectedRecipientCountry === 'string'
      ? selectedRecipientCountry
      : selectedRecipientCountry?.code;

    // Find the tenant by countryKey (not tenantKey) in tenantProperties
    const tenant = tenantProperties.find(
      (t: any) => t.countryKey === selectedCountryCode
    );

    this.maxParcelWeight.set(tenant?.maxParcelWeightThreshold ?? null);
    this.addValidator(this.maxParcelWeight());
  }

  /** Function to get responsible Location per tenant key */
  getResponsibleLocationKey() {
    let responsibleLocationKeyPerCountryKeyObject = this.responsibleLocationKeyPerCountryKeyString.filter(country => country.countryKey === this.createOrderForm.controls['requesterCountryCode'].value);
    if (responsibleLocationKeyPerCountryKeyObject.length) {
      this.responsibleLocationString = responsibleLocationKeyPerCountryKeyObject[0].responsibleLocationKey;
    }
  }

  /** Function for adding max weight validator to maxParcelWeight form control */
  addValidator(maxParcelWeight) {
    this.createOrderForm.controls['maxParcelWeight'].setValidators([Validators.required, Validators.max(maxParcelWeight), Validators.min(0.00000000001)]);
    this.createOrderForm.controls['maxParcelWeight'].updateValueAndValidity();
  }

  /**Building a form model*/
  initForm() {
    this.createOrderForm = this.formBuilder.group(
      {
        requesterName: ['', Validators.compose([Validators.required, this.noWhitespaceValidator])],
        requesterStreet: ['', Validators.compose([Validators.required, this.noWhitespaceValidator])],
        requesterHouseNumber: [''],
        requesterZipCode: ['', Validators.compose([Validators.required, this.noWhitespaceValidator])],
        requesterCity: ['', Validators.compose([Validators.required, this.noWhitespaceValidator])],
        requesterCountryCode: ['', Validators.compose([Validators.required, this.noWhitespaceValidator])],
        requesterPhone: ['', Validators.compose([Validators.pattern(this.PHONE_PATTERN)])],
        requesterEmail: ['', Validators.email],
        senderName: ['', Validators.compose([Validators.required, this.noWhitespaceValidator])],
        senderStreet: ['', Validators.compose([Validators.required, this.noWhitespaceValidator])],
        senderHouseNumber: [''],
        senderZipCode: ['', Validators.compose([Validators.required, this.noWhitespaceValidator])],
        senderCity: ['', Validators.compose([Validators.required, this.noWhitespaceValidator])],
        senderCountryCode: ['', Validators.compose([Validators.required, this.noNationalPaxOrders])],
        senderContactPersonName: ['', Validators.compose([Validators.required, this.noWhitespaceValidator])],
        senderPhone: ['', Validators.compose([Validators.required, Validators.pattern(this.PHONE_PATTERN)])],
        senderEmail: ['', Validators.email],
        senderComment: [''],
        recipientName: ['', Validators.compose([Validators.required, this.noWhitespaceValidator])],
        maxParcelWeight: ['', this.createNewOrder ? Validators.compose([Validators.required, Validators.min(0.00000000001)]) : []],
        recipientStreet: ['', Validators.compose([Validators.required, this.noWhitespaceValidator])],
        recipientHouseNumber: [''],
        recipientZipCode: ['', Validators.compose([Validators.required, this.noWhitespaceValidator])],
        recipientCity: ['', Validators.compose([Validators.required, this.noWhitespaceValidator])],
        recipientCountryCode: ['', Validators.required],
        recipientPhone: ['', Validators.compose([Validators.pattern(this.PHONE_PATTERN)])],
        recipientEmail: ['', Validators.email],
        recipientCheckbox: [false],
        requesterReference: [''],
        orderRequestedPickUpDate: ['', Validators.compose([Validators.required, this.weekendValidator])],
        orderNumberOfParcels: ['', Validators.required],
        orderIncoterm: [''],
        partnerParcelNumber: ['', Validators.compose([this.partnerParcelNumberValidator])],
      },
    );

    // Update maxParcelWeight validation when form initializes or on change
    if (!this.createNewOrder) {
      this.createOrderForm.get('maxParcelWeight')?.clearValidators(); // Remove validators when not creating a new order
    }
  }

  /**orderRequestedPickUpDate functionality :
   without parameter sets standardDate to tomorrow,
   with parameter adds the toAddDays to current date.
   The result date will be yyyy-mm-dd */
   getStandardDate(toAddDays: number = 0): string {
    const date = new Date();
    date.setDate(date.getDate() + toAddDays);
    return date.toISOString().split('T')[0];
  }

  excludeWeekends = (d: Date | null): boolean => {
    const day = (d || new Date()).getDay();
    return day !== 0 && day !== 6;
  }

  /**Function to get current Time*/
  getCurrentTime() {
    var date: any = new Date();
    var hours: any = date.getHours();
    var minutes: any = date.getMinutes();
    if (hours < 10) {
      hours = '0' + hours;
    }
    if (minutes < 10) {
      minutes = '0' + minutes;
    }
    this.currentTime.set(hours + ":" + minutes);
  }

  /**Function to get sender (pickup) country list*/
  validSenderList() {
    const senderCountryCodeArray: { code: string, label: string }[] = [];
    tenantProperties.forEach((tenant: any) => {
      if (tenant.countryKey && tenant.countryName) {
        senderCountryCodeArray.push({
          code: tenant.countryKey,
          label: `${tenant.countryKey} - ${tenant.countryName}`
        });
      }
    });

    // Remove duplicates by countryKey
    const uniqueCountries = senderCountryCodeArray.filter(
      (country, index, self) =>
        index === self.findIndex((c) => c.code === country.code)
    );

    this.senderCountryList.set(uniqueCountries.sort((a, b) => a.label.localeCompare(b.label)));
  }

  /**Function to set Default sender country*/
  setDefaultSenderCountry() {
    this.createOrderForm.patchValue({
      senderCountryCode: ''
    })
    this.validTenantTomorrowPickUpCutOffTimes(this.senderCountryList()[0]);
  }

  setDefaultRequesterCountry() {
    const tenant = tenantProperties.find((tenant: any) => tenant.tenantKey === this.tenantKey);
    if (tenant && tenant.countryKey) {
      this.createOrderForm.patchValue({
        requesterCountryCode: tenant.countryKey
      });
    }
  }

  /**Function to get requester country list*/
  validRequesterList() {
    // Only include countries with onboarded: true
    const onboardedCountries = tenantProperties
      .filter((tenant: any) => tenant.onboarded === true);

    // Flatten to unique countryKey-countryName pairs
    const requesterCountryCodeArray: { code: string, label: string }[] = [];
    onboardedCountries.forEach((tenant: any) => {
      if (tenant.countryKey && tenant.countryName) {
        requesterCountryCodeArray.push({
          code: tenant.countryKey,
          label: `${tenant.countryKey} - ${tenant.countryName}`
        });
      }
    });

    // Remove duplicates by countryKey
    const uniqueCountries = requesterCountryCodeArray.filter(
      (country, index, self) =>
        index === self.findIndex((c) => c.code === country.code)
    );

    this.requesterCountryList.set(uniqueCountries.sort((a, b) => a.label.localeCompare(b.label)));
  }

  /**onChange function for sender country dropdown to get valid recipient countries & to get valid TenantTomorrowPickUpCutOffTimes*/
  onChangeSenderCountry(event: any) {
    const selectedCountry = event.target.value
    this.setSelectedSenderTenant(selectedCountry);
    this.validRecipientList(selectedCountry);
    this.validTenantTomorrowPickUpCutOffTimes(selectedCountry);
  }

  /**Function to set the selectedSenderTenant based on the selected sender country*/
  setSelectedSenderTenant(selectedCountryCode: string) {
    const tenant = tenantProperties.find((tenant: any) => tenant.countryKey === selectedCountryCode);

    if (tenant && tenant.tenantKey) {
      this.selectedSenderTenant = tenant.tenantKey;
    } else {
      // If no specific tenant found, use a default mapping or the country code
      this.selectedSenderTenant = selectedCountryCode;
    }
  }

  /**Function to generate validMinOrderRequestedPickUpDate by clicking on orderRequestedPickUpDate*/
  onClickOrderRequestedPickUpDate() {
    this.validMaxOrderRequestedPickUpDate.set(this.getStandardDate(30));
    if (this.selectedSenderTenant == "GLS_DE") {
      if (this.currentTime() <= this.validTenantTomorrowPickUpCutOffTime) {
        this.validMinOrderRequestedPickUpDate.set(this.getStandardDate(0));
      } else {
        this.validMinOrderRequestedPickUpDate.set(this.getStandardDate(1));
      }
    }
    else if (this.currentTime() <= this.validTenantTomorrowPickUpCutOffTime) {
      this.validMinOrderRequestedPickUpDate.set(this.getStandardDate(1));
    } else {
      this.validMinOrderRequestedPickUpDate.set(this.getStandardDate(2));
    }
  }

  /** Getter  and Setter for date picker initial value */
  get orderRequestedPickUpDateValue() {
    return this.paxOrderData ? this.paxOrderData.requestedPickupDate : this.validMinOrderRequestedPickUpDate();
  }

  set orderRequestedPickUpDateValue(value: any) {
    if (this.paxOrderData) {
      this.paxOrderData.requestedPickupDate = value;
    } else {
      this.validMinOrderRequestedPickUpDate.set(value);
    }
  }

  /**Function to get valid TenantTomorrowPickUpCutOffTime*/
  validTenantTomorrowPickUpCutOffTimes(selectedSenderCountry: any) {
    const selectedCountryCode = typeof selectedSenderCountry === 'string'
      ? selectedSenderCountry
      : selectedSenderCountry?.code;

    const tenant = tenantProperties.find(
      (t: any) => t.countryKey === selectedCountryCode
    );

    this.validTenantTomorrowPickUpCutOffTime = tenant?.tomorrowPickUpCutOffTime || null;
  }

  /**Function to get recipient country list*/
  validRecipientList(selectedSenderCountry: any) {
    const recipientCountryCodeArray: { code: string, label: string }[] = [];
    tenantProperties.forEach((tenant: any) => {
      if (tenant.countryKey && tenant.countryName) {
        recipientCountryCodeArray.push({
          code: tenant.countryKey,
          label: `${tenant.countryKey} - ${tenant.countryName}`
        });
      }
    });

    // Remove duplicates by countryKey
    const uniqueCountries = recipientCountryCodeArray.filter(
      (country, index, self) =>
        index === self.findIndex((c) => c.code === country.code)
    );

    const newList = uniqueCountries.sort((a, b) => a.label.localeCompare(b.label));
    const currentList = this.recipientCountryList();

    // Check if lists are actually different
    const isDifferent = currentList.length !== newList.length ||
      currentList.some((country, index) => country.code !== newList[index].code);

    if (isDifferent) {
      this.recipientCountryList.set(newList);
    }
  }

  /**To get all createOrderForm controls*/
  get f(): { [key: string]: AbstractControl } {
    return this.createOrderForm.controls;
  }

  /**Function that sets the Recipient Input Fields disable*/
  setRecipientInputFieldsDisable() {
    this.createOrderForm.controls['recipientName'].disable();
    this.createOrderForm.controls['recipientStreet'].disable();
    this.createOrderForm.controls['recipientHouseNumber'].disable();
    this.createOrderForm.controls['recipientZipCode'].disable();
    this.createOrderForm.controls['recipientCity'].disable();
    this.createOrderForm.controls['recipientCountryCode'].disable();
    this.getResponsibleLocationKey();
  }

  /**Function that sets the Recipient Input Fields enable*/
  setRecipientInputFieldsEnable() {
    this.createOrderForm.controls['recipientName'].enable();
    this.createOrderForm.controls['recipientStreet'].enable();
    this.createOrderForm.controls['recipientHouseNumber'].enable();
    this.createOrderForm.controls['recipientZipCode'].enable();
    this.createOrderForm.controls['recipientCity'].enable();
    this.createOrderForm.controls['recipientCountryCode'].enable();
    this.getResponsibleLocationKey();
  }

  /**when clicking checkbox requesterEqualsRecipient, Recipient input fields will disable*/
  setRecipientEqualsRequesterCheckboxChecked() {
    if (this.createOrderForm.controls['recipientCheckbox'].value) {
      this.setRecipientInputFieldsDisable();
      this.createOrderForm.patchValue({
        recipientName: this.createOrderForm.controls['requesterName'].value,
        recipientStreet: this.createOrderForm.controls['requesterStreet'].value,
        recipientHouseNumber: this.createOrderForm.controls['requesterHouseNumber'].value,
        recipientZipCode: this.createOrderForm.controls['requesterZipCode'].value,
        recipientCity: this.createOrderForm.controls['requesterCity'].value,
        recipientCountryCode: this.createOrderForm.controls['requesterCountryCode'].value,
        recipientPhone: this.createOrderForm.controls['requesterPhone'].value,
        recipientEmail: this.createOrderForm.controls['requesterEmail'].value
      })
    } else {
      this.setRecipientInputFieldsEnable();
    }
  }

  /**function to check if recipient sender combination is correct for selected requester country*/
  checkSenderRecipientCombination() {
    const recipientCountryCodeValue = this.createOrderForm.controls['recipientCountryCode'].value;
    if (this.recipientCountryList().includes(recipientCountryCodeValue)) {
      this.createOrderForm.controls['requesterCountryCode'].value
    } else {
      this.senderRecipientInvalidAlert()
    }
  }

  /**function to get Modal for Invalid Sender Recipient combination*/
  senderRecipientInvalidAlert() {
    $('#staticBackdrop').modal('show');
  }

  /**function when click ok for invalid Sender Recipient combination*/
  confirmModal() {
    this.createOrderForm.patchValue({
      recipientCheckbox: false

    })
    this.setRecipientInputFieldsEnable();
  }

  fillFormWithEditPaxOrderData() {
    this.paxOrderData = null;
    this.paxOrder = localStorage.getItem("paxOrderForEdit");
    this.paxOrderData = JSON.parse(this.paxOrder);
    if (this.paxOrderData !== null) {
      this.slecetedPaxOrderKeyForEditOrder = this.paxOrderData['paxOrderKey'];
      this.editSelectedOrder.set(true);
      this.createNewOrder.set(false);
      this.createOrderForm.patchValue({
        requesterName: this.paxOrderData.requester['name'],
        requesterStreet: this.paxOrderData.requester['address'].street,
        requesterHouseNumber: this.paxOrderData.requester['address'].houseNumber,
        requesterZipCode: this.paxOrderData.requester['address'].zipCode,
        requesterCity: this.paxOrderData.requester['address'].city,
        requesterCountryCode: this.paxOrderData.requester['address'].countryCode,
        requesterPhone: this.paxOrderData.requester['phone'],
        requesterEmail: this.paxOrderData.requester['email'],

        senderName: this.paxOrderData.sender['name'],
        senderStreet: this.paxOrderData.sender['address'].street,
        senderHouseNumber: this.paxOrderData.sender['address'].houseNumber,
        senderZipCode: this.paxOrderData.sender['address'].zipCode,
        senderCity: this.paxOrderData.sender['address'].city,
        senderCountryCode: this.paxOrderData.sender['address'].countryCode,
        senderContactPersonName: this.paxOrderData.sender['contactPerson'],
        senderPhone: this.paxOrderData.sender['phone'],
        senderEmail: this.paxOrderData.sender['email'],

        recipientName: this.paxOrderData.recipient['name'],
        recipientStreet: this.paxOrderData.recipient['address'].street,
        recipientHouseNumber: this.paxOrderData.recipient['address'].houseNumber,
        recipientZipCode: this.paxOrderData.recipient['address'].zipCode,
        recipientCity: this.paxOrderData.recipient['address'].city,
        recipientCountryCode: this.paxOrderData.recipient['address'].countryCode,
        recipientPhone: this.paxOrderData.recipient['phone'],
        recipientEmail: this.paxOrderData.recipient['email'],

        requesterReference: this.paxOrderData.referenceRequester,
        incoterm: this.paxOrderData.incoterm,
        orderNumberOfParcels: this.paxOrderData.parcelCount,
      })

      // Set the selectedSenderTenant based on the loaded sender country
      this.setSelectedSenderTenant(this.paxOrderData.sender['address'].countryCode);
    }
  }

  /**function when click use Order as Template button in pax order list and append all the form values from list to inputs*/
  fillFormWithStoredPaxOrderData() {
    this.paxOrderData = null;
    this.paxOrder = localStorage.getItem("paxOrderToUseAsTemplate");
    this.paxOrderData = JSON.parse(this.paxOrder);
    if (this.paxOrderData !== null) {
      this.editSelectedOrder.set(false);
      this.createNewOrder.set(true);
      this.createOrderForm.patchValue({
        requesterName: this.paxOrderData.requester['name'],
        requesterStreet: this.paxOrderData.requester['address'].street,
        requesterHouseNumber: this.paxOrderData.requester['address'].houseNumber,
        requesterZipCode: this.paxOrderData.requester['address'].zipCode,
        requesterCity: this.paxOrderData.requester['address'].city,
        requesterCountryCode: this.paxOrderData.requester['address'].countryCode,
        requesterPhone: this.paxOrderData.requester['phone'],
        requesterEmail: this.paxOrderData.requester['email'],

        senderName: this.paxOrderData.sender['name'],
        senderStreet: this.paxOrderData.sender['address'].street,
        senderHouseNumber: this.paxOrderData.sender['address'].houseNumber,
        senderZipCode: this.paxOrderData.sender['address'].zipCode,
        senderCity: this.paxOrderData.sender['address'].city,
        senderCountryCode: this.paxOrderData.sender['address'].countryCode,
        senderContactPersonName: this.paxOrderData.sender['contactPerson'],
        senderPhone: this.paxOrderData.sender['phone'],
        senderEmail: this.paxOrderData.sender['email'],
        senderComment: this.paxOrderData.comment,

        recipientName: this.paxOrderData.recipient['name'],
        recipientStreet: this.paxOrderData.recipient['address'].street,
        recipientHouseNumber: this.paxOrderData.recipient['address'].houseNumber,
        recipientZipCode: this.paxOrderData.recipient['address'].zipCode,
        recipientCity: this.paxOrderData.recipient['address'].city,
        recipientCountryCode: this.paxOrderData.recipient['address'].countryCode,
        recipientPhone: this.paxOrderData.recipient['phone'],
        recipientEmail: this.paxOrderData.recipient['email'],

        requesterReference: this.paxOrderData.referenceRequester,
        incoterm: this.paxOrderData.incoterm
      })

      // Set the selectedSenderTenant based on the loaded sender country
      this.setSelectedSenderTenant(this.paxOrderData.sender['address'].countryCode);
    }
    localStorage.removeItem("paxOrderToUseAsTemplate");
  }

  fillFormFromOrderDetailsTemplate() {
    this.paxOrderData = null;
    this.paxOrderDetailsForEdit = null;
    this.paxOrderDetails = localStorage.getItem("seePaxOrderDetails");
    if (this.paxOrderDetails) {
      this.editSelectedOrder.set(false);
      this.createNewOrder.set(true);
    }
    this.paxOrderData = JSON.parse(this.paxOrderDetails);
    if (this.paxOrderData !== null) {
      this.slecetedPaxOrderKeyForEditOrder = this.paxOrderData['paxOrderKey'];
      this.createOrderForm.patchValue({
        requesterName: this.paxOrderData.requester['name'],
        requesterStreet: this.paxOrderData.requester['address'].street,
        requesterHouseNumber: this.paxOrderData.requester['address'].houseNumber,
        requesterZipCode: this.paxOrderData.requester['address'].zipCode,
        requesterCity: this.paxOrderData.requester['address'].city,
        requesterCountryCode: this.paxOrderData.requester['address'].countryCode,
        requesterPhone: this.paxOrderData.requester['phone'],
        requesterEmail: this.paxOrderData.requester['email'],

        senderName: this.paxOrderData.sender['name'],
        senderStreet: this.paxOrderData.sender['address'].street,
        senderHouseNumber: this.paxOrderData.sender['address'].houseNumber,
        senderZipCode: this.paxOrderData.sender['address'].zipCode,
        senderCity: this.paxOrderData.sender['address'].city,
        senderCountryCode: this.paxOrderData.sender['address'].countryCode,
        senderContactPersonName: this.paxOrderData.sender['contactPerson'],
        senderPhone: this.paxOrderData.sender['phone'],
        senderEmail: this.paxOrderData.sender['email'],
        senderComment: Array.isArray(this.paxOrderData.parcelDetails) && this.paxOrderData.parcelDetails.length > 0
          ? this.paxOrderData.parcelDetails[0].comment
          : '',
        recipientName: this.paxOrderData.recipient['name'],
        recipientStreet: this.paxOrderData.recipient['address'].street,
        recipientHouseNumber: this.paxOrderData.recipient['address'].houseNumber,
        recipientZipCode: this.paxOrderData.recipient['address'].zipCode,
        recipientCity: this.paxOrderData.recipient['address'].city,
        recipientCountryCode: this.paxOrderData.recipient['address'].countryCode,
        recipientPhone: this.paxOrderData.recipient['phone'],
        recipientEmail: this.paxOrderData.recipient['email'],

        requesterReference: this.paxOrderData.referenceRequester,
        incoterm: this.paxOrderData.incoterm
      })

      // Set the selectedSenderTenant based on the loaded sender country
      this.setSelectedSenderTenant(this.paxOrderData.sender['address'].countryCode);

      if(this.editSelectedOrder()) {
        this.paxOrderValdationsErrorList = JSON.parse(localStorage.getItem("paxOrderValdationsErrorList"));
        if(this.paxOrderValdationsErrorList) {
          this.invalidValidationAlert.set(true);
        }
        this.highlightinvalidtFields();
      }
    }
    this.orderRequestedPickUpDateValue = "";

    localStorage.removeItem("paxOrderValdationsErrorList");
  }

  highlightinvalidtFields() {
    this.paxOrderValdationsErrorList['validationErrors']
      .filter(paxOrderValidationError =>
        !paxOrderValidationError['fieldName'].toLowerCase().includes('and') &&
        !paxOrderValidationError['fieldName'].toLowerCase().includes('Pick') &&
        !paxOrderValidationError['fieldName'].toLowerCase().includes('Authentication') &&
        !paxOrderValidationError['fieldName'].toLowerCase().includes('contactId')
      )
      .forEach(paxOrderValidationError => {
        const fieldName = this.transFieldNames(paxOrderValidationError['fieldName']);
        const control = this.createOrderForm.controls[fieldName];
        if (control) {
          control.addValidators(this.paxOrderErrorValidatior);
          control.updateValueAndValidity();
        } else {
          console.warn(`Control for ${fieldName} not found.`);
        }
      });
  }
  transFieldNames(input: string) : string{
    let tmp =  input.replace("recipient", "requester")
        .split(".")
        .filter(part => part !== "address")
        .map((part, index, array) => index === array.length - 1 ? part.charAt(0).toUpperCase() + part.slice(1) : part)
        .join("");
    return tmp;
  }


  formatShipItErrors(item: any): string {
    if (item['validationErrors'].length < 1) {
      return "Order is still being processed, check again later."
    }

    var formattedErrors = "The following values contain validation errors and need to be resolved: \n\n";

    return formattedErrors += item['validationErrors'].map((error: any) =>
      `${this.replaceTechnicalWithUINames(error.fieldName)}: ${error.fieldValue}`
    ).join('\n\n');
  }

  replaceTechnicalWithUINames(fieldName: string): string {
    return fieldName.replace("recipient", "requester").replace("sender", "pickup");
  }

  /**function to check the checkbox Recepient quals Requester when the both form values are eaual*/
  setRecipientEqualsRequesterChecked(paxOrderData: any) {
    if (paxOrderData.requesterName === paxOrderData.deliveryName &&
      paxOrderData.requesterStreet === paxOrderData.deliveryStreet &&
      paxOrderData.requesterHouseNumber === paxOrderData.deliveryHouseNumber &&
      paxOrderData.requesterZipCode === paxOrderData.deliveryZipCode &&
      paxOrderData.requesterCity === paxOrderData.deliveryCity &&
      paxOrderData.requesterCountry === paxOrderData.deliveryCountry) {
      this.createOrderForm.patchValue({
        recipientCheckbox: true
      })
      this.setRecipientInputFieldsDisable();
    }
  }
  /**Submitting createOderForm*/
  onSubmit(): void {
    // Prevent multiple submissions
    if (this.isSubmitting()) {
      return;
    }

    var formData: any = this.createOrderForm.value;
    const nextOrderRequestedPickUpDate = new Date(formData.orderRequestedPickUpDate);
    const postData: any = {
      requester: {
        name: formData.requesterName ? formData.requesterName.trim() : '',
        address: {
          street: formData.requesterStreet ? formData.requesterStreet.trim() : '',
          houseNumber: formData.requesterHouseNumber ? formData.requesterHouseNumber.trim() : '',
          zipCode: formData.requesterZipCode ? formData.requesterZipCode.trim() : '',
          city: formData.requesterCity ? formData.requesterCity.trim() : '',
          countryCode: formData.requesterCountryCode,
        },
        email: formData.requesterEmail,
        phone: formData.requesterPhone,
      },
      sender: {
        name: formData.senderName ? formData.senderName.trim() : '',
        contactPerson: formData.senderContactPersonName ? formData.senderContactPersonName.trim() : '',
        address: {
          street: formData.senderStreet ? formData.senderStreet.trim() : '',
          houseNumber: formData.senderHouseNumber ? formData.senderHouseNumber.trim() : '',
          zipCode: formData.senderZipCode ? formData.senderZipCode.trim() : '',
          city: formData.senderCity ? formData.senderCity.trim() : '',
          countryCode: formData.senderCountryCode,
        },
        phone: formData.senderPhone,
        email: formData.senderEmail,
      },
      recipient: {
        name: formData.recipientCheckbox ? formData.requesterName.trim() : formData.recipientName.trim(),
        address: {
          street: formData.recipientCheckbox ? formData.requesterStreet.trim() :  formData.recipientStreet ? formData.recipientStreet.trim() : '',
          houseNumber: formData.recipientCheckbox ? formData.requesterHouseNumber.trim() : formData.recipientHouseNumber ? formData.recipientHouseNumber.trim() : '',
          zipCode: formData.recipientCheckbox ? formData.requesterZipCode.trim() : formData.recipientZipCode ? formData.recipientZipCode.trim(): '',
          city: formData.recipientCheckbox ? formData.requesterCity.trim() : formData.recipientCity ? formData.recipientCity.trim() : '',
          countryCode: formData.recipientCheckbox ? formData.requesterCountryCode : formData.recipientCountryCode,
        },
        phone: formData.recipientPhone,
        email: formData.recipientEmail,
      },
      parcelCount: formData.orderNumberOfParcels,
      requestedPickupDate: formData.orderRequestedPickUpDate ? this.formatDate(nextOrderRequestedPickUpDate) : null,
      referenceRequester: formData.requesterReference ? formData.requesterReference.toString().trim() : '',
      incoterm: formData.orderIncoterm ? formData.orderIncoterm.trim() : '',
      orderRequestLocation: this.responsibleLocationString,
      comment: formData.senderComment ? formData.senderComment.trim() : '',
      maxParcelWeight: formData.maxParcelWeight,
      partnerParcelNumber: formData.partnerParcelNumber ? formData.partnerParcelNumber.trim() : '',
    }
    this.submitted.set(true);
    // Ignore maxParcelWeight validation if createNewOrder is false
    if (!this.createNewOrder()) {
      this.createOrderForm.get('maxParcelWeight')?.clearValidators();
      this.createOrderForm.get('maxParcelWeight')?.updateValueAndValidity(); // Update form validity after clearing validators
    }

    if (this.createOrderForm.valid) {
      this.isSubmitting.set(true); // Set flag to prevent multiple submissions

      // Set timeout as safety net (30 seconds)
      this.submissionTimeout = setTimeout(() => {
        console.warn('Submission timeout reached, resetting isSubmitting flag');
        this.isSubmitting.set(false);
      }, 30000);

      if(this.createNewOrder()) {
        this.paxOrderService.createOrder(postData)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
          this.dangerAlert.set(false);
          this.invalidAlert.set(false);
          this.invalidValidationAlert.set(false);
          this.createOrderForm.reset({
            requesterReference: '',
            orderIncoterm: ''
          });
          this.submitted.set(false);
          this.setRecipientEqualsRequesterCheckboxChecked();
          this.setDefaultSenderCountry();
          this.setDefaultRequesterCountry();

          this.lastCreatedPaxOrderKey = response;
          if (response && response["parcels"] && response["parcels"][0] && response["parcels"][0]["parcelNumber"]) {
            this.showFadingSuccessMessage('Order created with GLS parcel number ' + response["parcels"][0]["parcelNumber"]);
          } else {
            this.showFadingSuccessMessage('Order was created successfully but could not display the GLS parcel number!');
          }
          this.clearSubmissionState(); // Reset flag after successful submission
            },
            error: (errorResponse) => {
          this.errorMessage.set(errorResponse.error.errors.map((senderCountryCode: any) => senderCountryCode))
          this.dangerAlert.set(true);
          this.successAlert.set(false);
          this.invalidAlert.set(false);
          this.invalidValidationAlert.set(false);
          this.clearSubmissionState();
            }
          });
      } else if (this.editSelectedOrder()) {
        this.paxOrderService.editOrder(this.slecetedPaxOrderKeyForEditOrder, postData)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
          if (response === null) {
            this.showFadingSuccessMessage('The changes are successfully saved!');
            this.dangerAlert.set(false);
            this.invalidAlert.set(false);
            this.invalidValidationAlert.set(false);
            this.createOrderForm.reset({
            requesterReference: '',
            orderIncoterm: ''
          });
          this.submitted.set(false);
          this.setRecipientEqualsRequesterCheckboxChecked();
          this.setDefaultSenderCountry();
          this.setDefaultRequesterCountry();
          this.clearSubmissionState();

          } else {
            this.showFadingErrorMessage()
            this.clearSubmissionState();
          }
            },
            error: (error) => {
          console.error('Error editting order:', error);
          this.clearSubmissionState();
            }
          });
      }
    } else {
      this.invalidAlert.set(true);
      this.successAlert.set(false);
      this.dangerAlert.set(false);
      this.clearSubmissionState();
    }
  }

  /**closing the SuccessAlert*/
  closeSuccessAlert() {
    this.successAlert.set(false);
  }

  /**closing the DangerAlert*/
  closeDangerAlert() {
    this.dangerAlert.set(false);
  }

  /**closing the InvalidAlert*/
  closeInvalidAlert() {
    this.invalidAlert.set(false);
  }

  closeInvalidValidationAlert() {
    this.invalidValidationAlert.set(false);
  }

  formatDate(date: Date): string {
    let d = new Date(date.getTime() - date.getTimezoneOffset() * 60000); //Remove offset due to UTC and local time issue
    return d.toISOString().split('T')[0];
  }

  showFadingSuccessMessage(text): void {
    this.message.set(text);
    this.messageType.set('success');
    this.displayDuration.set(10000);
    this.fadeDuration.set(10000);
  }

  showFadingErrorMessage(): void {
    this.message.set('An error has occurred!');
    this.messageType.set('error');
    this.displayDuration.set(10000);
    this.fadeDuration.set(10000);
  }

  goBack() {
    this.location.back();
  }

  /**Helper method to clear submission state and timeout*/
  private clearSubmissionState(): void {
    this.isSubmitting.set(false);
    if (this.submissionTimeout) {
      clearTimeout(this.submissionTimeout);
      this.submissionTimeout = null;
    }
  }



  /**
   * Manual date formatting to ensure DD.MM.YYYY format without locale issues
   * Preserves original format style (no leading zeros if user didn't type them)
   */
  private formatDateManual(date: Date, preserveOriginalFormat?: string): string {
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    // If we have the original format, try to preserve the style (with/without leading zeros)
    if (preserveOriginalFormat && (preserveOriginalFormat.includes('/') || preserveOriginalFormat.includes('.'))) {
      const separator = preserveOriginalFormat.includes('/') ? '/' : '.';
      const parts = preserveOriginalFormat.split(separator);
      if (parts.length === 3) {
        const originalDay = parts[0];
        const originalMonth = parts[1];

        // Preserve leading zero style from original input
        const dayStr = originalDay.length === 1 ? day.toString() : day.toString().padStart(2, '0');
        const monthStr = originalMonth.length === 1 ? month.toString() : month.toString().padStart(2, '0');

        return `${dayStr}/${monthStr}/${year}`;
      }
    }

    // Default: no leading zeros (user-friendly format) - DD/MM/YYYY
    return `${day}/${month}/${year}`;
  }

  /**
   * Helper method to format date for display consistently using German format DD.MM.YYYY
   */
  formatDateForDisplay(date: Date | string): string | null {
    if (!date) {
      return null;
    }

    try {
      // If it's already a Date object, format it
      if (date instanceof Date) {
        return this.datePipe.transform(date, 'dd/MM/yyyy');
      }

      // If it's a string, try to parse and format
      const parsedDate = this.parseAndValidateDate(date);
      if (parsedDate) {
        return this.datePipe.transform(parsedDate, 'dd/MM/yyyy');
      }

      return null;
    } catch (error) {
      console.error('Error in formatDateForDisplay:', error, 'Input:', date);
      return null;
    }
  }

  /**
   * Function to parse and validate date input handling various formats
   * Supports: Date objects, ISO strings, DD.MM.YYYY, DD/MM/YYYY, YYYY-MM-DD
   * Prevents day/month swapping by enforcing German locale expectations
   */
  parseAndValidateDate(dateInput: Date | string): Date | null {
    if (!dateInput) {
      return null;
    }

    // If already a Date object, validate and return
    if (dateInput instanceof Date) {
      return isNaN(dateInput.getTime()) ? null : dateInput;
    }

    // Handle string inputs
    let dateString = dateInput.toString().trim();

    if (!dateString) {
      return null;
    }

    let parsedDate: Date;

    try {
      // Try different date formats with proper validation
      if (dateString.includes('.')) {
        // Handle DD.MM.YYYY format (German format) - PRIORITY FORMAT
        const parts = dateString.split('.');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
          const year = parseInt(parts[2], 10);

          // Smart validation: detect likely month/day swapping for ambiguous cases
          // If day > 12 and month <= 12, it's clearly DD.MM format (correct)
          // If day <= 12 and month > 12, it's likely MM.DD format (user error)
          // If both day <= 12 and month <= 12, it's ambiguous but we follow DD.MM.YYYY format

          if (day > 12 && month >= 0 && month <= 11) {
            // Clear case: day > 12, so it must be DD.MM.YYYY format
          } else if (day <= 12 && (month + 1) > 12) {
            // Likely user error: month > 12 suggests they meant MM.DD format
            // Swap day and month to correct the user's likely intention
            const correctedDay = month + 1; // Convert back from 0-indexed
            const correctedMonth = day - 1; // Convert to 0-indexed

            // Validate the corrected values
            if (correctedDay >= 1 && correctedDay <= 31 && correctedMonth >= 0 && correctedMonth <= 11 && year >= 1900 && year <= 2100) {
              parsedDate = new Date(year, correctedMonth, correctedDay, 12, 0, 0, 0);

              // Validate the corrected date actually exists
              if (parsedDate.getDate() === correctedDay && parsedDate.getMonth() === correctedMonth && parsedDate.getFullYear() === year) {
                return parsedDate;
              } else {
                throw new Error('Invalid date - corrected date does not exist');
              }
            } else {
              throw new Error('Corrected date parts out of range');
            }
          } else if (day >= 1 && day <= 31 && month >= 0 && month <= 11 && year >= 1900 && year <= 2100) {
            // Standard DD.MM.YYYY validation (both day and month are valid ranges)
            parsedDate = new Date(year, month, day, 12, 0, 0, 0); // Set to noon to avoid timezone issues

            // Additional validation: ensure the date actually exists (handles Feb 30, etc.)
            if (parsedDate.getDate() === day && parsedDate.getMonth() === month && parsedDate.getFullYear() === year) {
              return parsedDate;
            } else {
              throw new Error('Invalid date - date does not exist');
            }
          } else {
            throw new Error('Date parts out of range');
          }
        } else {
          throw new Error('Invalid DD.MM.YYYY format');
        }
      } else if (dateString.includes('/')) {
        // Handle DD/MM/YYYY format - treating as DD/MM/YYYY (day first, European style)
        const parts = dateString.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
          const year = parseInt(parts[2], 10);

          // Validate ranges
          if (day >= 1 && day <= 31 && month >= 0 && month <= 11 && year >= 1900 && year <= 2100) {
            parsedDate = new Date(year, month, day, 12, 0, 0, 0);

            // Validate the date actually exists
            if (parsedDate.getDate() === day && parsedDate.getMonth() === month && parsedDate.getFullYear() === year) {
              return parsedDate;
            } else {
              throw new Error('Invalid date - date does not exist');
            }
          } else {
            throw new Error('Date parts out of range');
          }
        } else {
          throw new Error('Invalid DD/MM/YYYY format');
        }
      } else if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Handle YYYY-MM-DD format (ISO format)
        const parts = dateString.split('-');
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
        const day = parseInt(parts[2], 10);

        if (day >= 1 && day <= 31 && month >= 0 && month <= 11 && year >= 1900 && year <= 2100) {
          parsedDate = new Date(year, month, day, 12, 0, 0, 0);

          if (parsedDate.getDate() === day && parsedDate.getMonth() === month && parsedDate.getFullYear() === year) {
            return parsedDate;
          } else {
            throw new Error('Invalid date - date does not exist');
          }
        } else {
          throw new Error('Date parts out of range');
        }
      } else {
        // Try standard Date parsing as last resort (handles ISO strings, etc.)
        parsedDate = new Date(dateString);

        // For standard parsing, be extra careful about invalid dates
        if (isNaN(parsedDate.getTime())) {
          throw new Error('Standard parsing failed');
        }

        // Set to noon to avoid timezone issues
        parsedDate.setHours(12, 0, 0, 0);
        return parsedDate;
      }

    } catch (error) {
      return null;
    }

    return null;
  }

  /**
   * Handle date input changes for real-time feedback
   */
  onOrderRequestedPickUpDateInput(event: any) {
    const currentValue = event.target?.value?.trim();
    if (currentValue) {
      this.lastUserDateInput = currentValue;
    }
  }

  /**
   * Handle date blur event - validate and format on focus loss for orderRequestedPickUpDate
   */
  onOrderRequestedPickUpDateBlur(event: any) {
    // Prevent multiple blur event processing
    if (this.processingDateBlur) {
      return;
    }

    this.processingDateBlur = true;
    const rawInputValue = event.target?.value?.trim();
    const inputValue = this.lastUserDateInput || rawInputValue;

    if (!inputValue || inputValue === '') {
      this.createOrderForm.patchValue({ orderRequestedPickUpDate: null });
      this.lastUserDateInput = '';
      setTimeout(() => {
        this.processingDateBlur = false;
      }, 50);
      return;
    }

    const parsedDate = this.parseAndValidateDate(inputValue);

    if (parsedDate) {
      // Use manual formatting to preserve user's format style
      const formattedDate = this.formatDateManual(parsedDate, inputValue);

      if (formattedDate && event.target) {
        // Set the formatted value in the DOM
        event.target.value = formattedDate;
        event.target.setAttribute('value', formattedDate);

        // Trigger input event to ensure Angular detects the change
        const inputEvent = new Event('input', { bubbles: true });
        event.target.dispatchEvent(inputEvent);

        // Anti-interference protection with single timeout
        setTimeout(() => {
          if (event.target && event.target.value !== formattedDate) {
            event.target.value = formattedDate;
            event.target.setAttribute('value', formattedDate);
            this.createOrderForm.get('orderRequestedPickUpDate')?.setValue(parsedDate, { emitEvent: false });
          }
        }, 50);
      }

      this.createOrderForm.patchValue({ orderRequestedPickUpDate: parsedDate });

      setTimeout(() => {
        this.processingDateBlur = false;
        this.lastUserDateInput = '';
      }, 50);
    } else {
      // Invalid date - revert to previous valid value or clear
      const currentDate = this.createOrderForm.get('orderRequestedPickUpDate')?.value;

      if (currentDate && currentDate instanceof Date) {
        const formattedDate = this.datePipe.transform(currentDate, 'dd/MM/yyyy');
        if (formattedDate && event.target) {
          event.target.value = formattedDate;
        }
      } else {
        // No valid current date, clear the field
        if (event.target) {
          event.target.value = '';
        }
        this.createOrderForm.patchValue({ orderRequestedPickUpDate: null });
      }

      setTimeout(() => {
        this.processingDateBlur = false;
        this.lastUserDateInput = '';
      }, 50);
    }
  }

  /**
   * Handle keydown events for date inputs to provide better UX
   */
  onOrderRequestedPickUpDateKeydown(event: KeyboardEvent) {
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End'
    ];

    // Allow numbers and dots for DD.MM.YYYY format
    const isNumber = (event.key >= '0' && event.key <= '9');
    const isDot = event.key === '.';
    const isSlash = event.key === '/';

    if (!allowedKeys.includes(event.key) && !isNumber && !isDot && !isSlash) {
      event.preventDefault();
    }
  }

  /**
   * Handle Enter key press on date inputs - validate and apply the date
   */
  onOrderRequestedPickUpDateEnterPressed(event: any) {
    // Trigger blur behavior when Enter is pressed to validate the input
    this.onOrderRequestedPickUpDateBlur(event);
    // Remove focus from the input to hide the datepicker
    event.target.blur();
  }

  /**
   * Handle datepicker closed event to apply formatting
   */
  onOrderRequestedPickUpDatePickerClosed() {
    const currentValue = this.createOrderForm.get('orderRequestedPickUpDate')?.value;

    if (currentValue && currentValue instanceof Date) {
      const inputElement = document.querySelector('input[formControlName="orderRequestedPickUpDate"]') as HTMLInputElement;
      if (inputElement) {
        const formattedDate = this.datePipe.transform(currentValue, 'dd/MM/yyyy');
        if (formattedDate) {
          inputElement.value = formattedDate;

          // Additional timeout to ensure formatting persists
          setTimeout(() => {
            if (inputElement && inputElement.value !== formattedDate) {
              inputElement.value = formattedDate;
            }
          }, 100);
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearSubmissionState();
  }
}
